import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'lib/store';
import { EligibilityCase } from 'utils/medicareSupEligibility';

export interface Eligibility {
  birthdate: string;
  citizen: string;
  collectingRR: string;
  collectingSS: string;
  countyFips: string;
  currentInsuranceExpQ: string;
  currentInsuranceQ: string;
  currentlyMarried: string;
  fileTaxes: string;
  gender: string;
  hasHSA?: string;
  hundredPlusEmployees: string;
  illnessDisability: Array<string>;
  income: number;
  livesInUS: string;
  prevSpouseStatus: string;
  prevSpouseWorked10Yrs: string;
  tobacco: string;
  twentyPlusEmployees: string;
  worked10Yrs: string;
  worked30Quarters: string;
  zipCode: string;
}
export interface EligibilitySlice {
  completedEligibility: boolean;
  gatedContentDestination?: string;
  renderedQuestions: string[];
  sep: SEPEligibility;
  values: Eligibility;
  eligibilityCase: EligibilityCase | null;
}

export interface SEPEligibility {
  coverageStatus: 'lost' | 'losing' | null;
  lossDate: string;
  enrollmentEndDate: string;
  isEligible: boolean;
}

export const initialEligibilityState: EligibilitySlice = {
  completedEligibility: false,
  gatedContentDestination: '',
  renderedQuestions: ['birthdate', 'livesInUS', 'zipCode'],
  sep: {
    coverageStatus: null,
    lossDate: '',
    enrollmentEndDate: '',
    isEligible: false,
  },
  values: {
    birthdate: '',
    citizen: '',
    collectingRR: '',
    collectingSS: '',
    countyFips: '',
    currentInsuranceExpQ: '',
    currentInsuranceQ: '',
    currentlyMarried: '',
    fileTaxes: '',
    gender: '',
    hasHSA: '',
    hundredPlusEmployees: '',
    illnessDisability: [],
    income: 0,
    livesInUS: '',
    prevSpouseStatus: '',
    prevSpouseWorked10Yrs: '',
    tobacco: '',
    twentyPlusEmployees: '',
    worked10Yrs: '',
    worked30Quarters: '',
    zipCode: '',
  },
  eligibilityCase: null,
};

const eligibilitySlice = createSlice({
  initialState: initialEligibilityState,
  name: 'eligibility',
  reducers: {
    setCompletedEligibility: (state) => {
      return {
        ...state,
        completedEligibility: true,
        values: {
          ...state.values,
        },
      };
    },
    setSEPEligibility: (state, action: PayloadAction<SEPEligibility>) => {
      return {
        ...state,
        sep: {
          ...action.payload,
        },
      };
    },
    setEligibilityValues: (
      state,
      action: PayloadAction<EligibilitySlice['values']>
    ) => {
      return {
        ...state,
        renderedQuestions: [...state.renderedQuestions],
        values: {
          ...action.payload,
        },
      };
    },
    setGatedContentDestination: (
      state,
      action: PayloadAction<EligibilitySlice['gatedContentDestination']>
    ) => {
      return {
        ...state,
        gatedContentDestination: action.payload,
        values: {
          ...state.values,
        },
      };
    },
    setRecommendationValues: (state, action) => {
      state.values.birthdate = action.payload.birthdate;
      state.values.zipCode = action.payload.zipCode;
      state.values.gender = action.payload.gender;
      state.values.tobacco = action.payload.tobacco;
      state.values.countyFips = action.payload.countyFips;
    },
    setRenderedEligibilityQuestion: (
      state,
      action: PayloadAction<EligibilitySlice['renderedQuestions']>
    ) => {
      return {
        ...state,
        renderedQuestions: [...action.payload],
        values: {
          ...state.values,
        },
      };
    },
    setEligibilityCase: (
      state,
      action: PayloadAction<EligibilityCase | null>
    ) => {
      return {
        ...state,
        eligibilityCase: action.payload,
      };
    },
  },
});

const { actions, reducer } = eligibilitySlice;

export const {
  setSEPEligibility,
  setGatedContentDestination,
  setEligibilityCase,
} = actions;

export const selectSEPEligibility = (state: RootState) => state.eligibility.sep;

export const selectEligibilityCase = (state: RootState) =>
  state.eligibility.eligibilityCase;

export default reducer;
